import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { useRole } from '../../hooks/useRole';
import { entityPath } from '../../utils/entityRoutes';
import type { ReadyShipmentSummary } from '../../types/Procurement';
import '../../styles/components/ProcurementScreens.css';
import '../../styles/components/ReadyToReceive.css';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
export const shipmentLabel = (s: ReadyShipmentSummary): string => s.waybill_no || s.container_ref || s.shipment_id.slice(0, 8).toUpperCase();

interface Props {
  shipments: ReadyShipmentSummary[];
  /** 'summary' is one line with a button to open the list; 'list' names every waiting shipment. */
  mode: 'summary' | 'list';
  onShowList?: () => void;
}

/** Shipments that have arrived and still have goods nobody has confirmed. Nothing is shown when there are none. */
const ReadyToReceiveBanner: React.FC<Props> = ({ shipments, mode, onShowList }) => {
  const navigate = useNavigate();
  const { isPlanner, isContractor } = useRole();
  const canConfirm = isPlanner && !isContractor;
  if (shipments.length === 0) return null;

  const heading = `${plural(shipments.length, 'shipment has', 'shipments have')} goods ready to receive`;
  if (mode === 'summary') {
    return (
      <section className="proc-panel rtr-banner rtr-banner--summary" aria-label="Shipments ready to receive">
        <h3 className="heading-card">{heading}</h3>
        <Button variant="secondary" size="sm" onClick={onShowList}>Show them</Button>
      </section>
    );
  }

  return (
    <section className="proc-panel rtr-banner" aria-label="Shipments ready to receive">
      <h3 className="heading-card">{heading}</h3>
      {shipments.map(s => (
        <div key={s.shipment_id} className="rtr-banner-row">
          <span>
            <strong>{shipmentLabel(s)}</strong>
            <span className="text-subtle"> · {s.progress_status} · {plural(s.order_count, 'order', 'orders')}, {plural(s.line_count, 'line', 'lines')}</span>
          </span>
          <Button variant={canConfirm ? 'primary' : 'secondary'} size="sm" onClick={() => navigate(`${entityPath('shipment', s.shipment_id) ?? '/procurement/shipments'}#ready-to-receive`)}>
            {canConfirm ? 'Review and confirm' : 'View'}
          </Button>
        </div>
      ))}
    </section>
  );
};

export default ReadyToReceiveBanner;
